import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Button, TextField, Typography, Alert, Stack, Table, TableBody, TableCell,
  TableRow, TableHead, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Skeleton,
} from '@mui/material'
import TableViewport from '../components/common/TableViewport'
import CopyButton from '../components/common/CopyButton'
import FilterPillRow from '../components/common/FilterPillRow'
import HoldButton from '../components/common/confirm/HoldButton'
import { StatusChip } from '../rma/constants'
import { FS, SP, PANEL_SX } from '../theme/tokens'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL

function BucketTable({ data = {} }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableBody>
          {Object.entries(data).map(([t, n]) => (
            <TableRow key={t}>
              <TableCell>{t}</TableCell>
              <TableCell align="right">{n}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

// Debounced cross-source customer search feeding the "new request" email field.
function CustomerSearchField({ onPick }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [err, setErr] = useState(false)
  const [touched, setTouched] = useState(false)
  const timer = useRef(null)

  const onChange = (val) => {
    setQ(val)
    if (timer.current) clearTimeout(timer.current)
    if (val.trim().length < 3) { setResults([]); setTouched(false); setErr(false); return }
    setSearching(true); setTouched(true); setErr(false)
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/admin/privacy/search`, { params: { q: val.trim() } })
        setResults(r.data.results || [])
      } catch {
        setResults([]); setErr(true)
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  const showPanel = touched && q.trim().length >= 3
  return (
    <Box sx={{ position: 'relative', maxWidth: 520 }}>
      <TextField
        label="Search customer (email or name)"
        type="search"
        value={q}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        role="combobox"
        aria-expanded={showPanel && results.length > 0}
      />
      {showPanel && (
        <Box role="listbox" sx={{ ...PANEL_SX, position: 'absolute', zIndex: 5, mt: 0.5, width: '100%', maxHeight: 260, overflowY: 'auto' }}>
          {searching ? (
            <Typography sx={{ p: SP.tight, fontSize: FS.sm, color: 'text.secondary' }}>Searching…</Typography>
          ) : err ? (
            <Typography sx={{ p: SP.tight, fontSize: FS.sm, color: 'error.main' }}>Search failed.</Typography>
          ) : results.length === 0 ? (
            <Typography sx={{ p: SP.tight, fontSize: FS.sm, color: 'text.secondary' }}>No matches.</Typography>
          ) : results.map((r) => (
            <Box
              key={r.email}
              component="button"
              type="button"
              role="option"
              onClick={() => { onPick(r); setQ(''); setResults([]); setTouched(false) }}
              sx={{
                display: 'block', width: '100%', textAlign: 'left', border: 0, bgcolor: 'transparent',
                cursor: 'pointer', p: SP.tight,
                '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
              }}
            >
              <Typography sx={{ fontSize: FS.md }}>{r.email}</Typography>
              <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                {r.name || '—'} · {(r.sources || []).join(', ')}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default function PrivacyErasePanel() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const statusFilter = params.get('status') || 'open'
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [openCount, setOpenCount] = useState(0)
  const [review, setReview] = useState(null)     // { request, preview } | null
  const [rejectFor, setRejectFor] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [reqs, cnt] = await Promise.all([
        axios.get(`${API}/api/admin/privacy/requests`, { params: { status: statusFilter } }),
        axios.get(`${API}/api/admin/privacy/requests/count-open`),
      ])
      setRequests(reqs.data.requests || [])
      setOpenCount(cnt.data.count || 0)
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Requests could not be loaded')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => { load() }, [load])

  const openReview = async (req) => {
    setBusy(true)
    try {
      const r = await axios.get(`${API}/api/admin/privacy/preview`, {
        params: { email: req.email || '', shopify_customer_id: req.shopify_customer_id || '' },
      })
      setReview({ request: req, preview: r.data })
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Preview failed')
    } finally {
      setBusy(false)
    }
  }

  const runExecute = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/privacy/execute`, {
        request_id: review.request.id,
        email: review.request.email,
        confirm: true,
      })
      toast.success(`Deleted. Request #${r.data.request_id}.`)
      setReview(null)
      load()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Deletion failed')
    }
  }

  const submitReject = async () => {
    if (!rejectReason.trim()) return
    try {
      await axios.post(`${API}/api/admin/privacy/requests/${rejectFor.id}/reject`, { reason: rejectReason.trim() })
      toast.success('Rejected.')
      setRejectFor(null); setRejectReason('')
      load()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Rejection failed')
    }
  }

  const createRequest = async (email) => {
    const e = (email || '').trim()
    if (!e) return
    try {
      await axios.post(`${API}/api/admin/privacy/requests`, { email: e })
      toast.success('Request created.')
      setNewEmail('')
      setParams({ status: 'open' })
      load()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Creation failed')
    }
  }

  const filterOptions = [
    { value: 'open', label: 'Open', count: openCount },
    { value: 'executed', label: 'Executed' },
    { value: 'rejected', label: 'Rejected' },
  ]

  const totalAnon = review ? Object.values(review.preview.to_anonymize || {}).reduce((a, b) => a + b, 0) : 0
  const totalRestrict = review ? Object.values(review.preview.to_restrict || {}).reduce((a, b) => a + b, 0) : 0
  const draft = review?.request?.draft_json || null

  // Kopf und Einzug kommen von `AdminLayout` (Abschnittsname als `h1`,
  // Brotkrume „Admin › …", Tab-Leiste, `PageContainer`). Die eine Aktion des
  // Kopfes steht jetzt als
  // Knopf oben rechts im Inhalt — W-4 der Gates zu PR 4: die Seite ist
  // englisch ("New request", "Open", "Executed"), der Knopf ist es auch.
  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: SP.tight }}>
        <Button variant="outlined" onClick={() => navigate('/admin/gdpr-wipe')}>
          Manual erase
        </Button>
      </Stack>

      <Box sx={{ ...PANEL_SX, mb: SP.section }}>
        <Typography sx={{ fontSize: FS.lg, fontWeight: 600, mb: SP.tight }}>New request</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight} sx={{ alignItems: { sm: 'center' } }}>
          <CustomerSearchField onPick={(r) => createRequest(r.email)} />
          <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>or</Typography>
          <TextField
            label="Email directly"
            type="email"
            size="small"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <Button variant="outlined" disabled={!newEmail.trim()} onClick={() => createRequest(newEmail)}>
            Create request
          </Button>
        </Stack>
      </Box>

      <FilterPillRow
        alwaysShow
        options={filterOptions}
        value={statusFilter}
        onChange={(v) => setParams({ status: v })}
      />

      <Box sx={{ ...PANEL_SX, mt: SP.tight }}>
        {loading ? (
          <Stack spacing={SP.tight}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
          </Stack>
        ) : requests.length === 0 ? (
          <Typography sx={{ fontSize: FS.md, color: 'text.secondary' }}>
            No requests in this view.
          </Typography>
        ) : (
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Typography sx={{ fontSize: FS.md }}>{r.email}</Typography>
                      {r.customer_name && (
                        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>{r.customer_name}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip label={r.source} size="small" /></TableCell>
                    <TableCell sx={{ fontSize: FS.sm }}>{(r.requested_at || '').slice(0, 10)}</TableCell>
                    <TableCell><StatusChip status={r.status} /></TableCell>
                    <TableCell align="right">
                      {r.status === 'requested' && (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button size="small" variant="outlined" disabled={busy} onClick={() => openReview(r)}>Review</Button>
                          <Button size="small" color="error" onClick={() => setRejectFor(r)}>Reject</Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableViewport>
        )}
      </Box>

      {/* Review dialog — preview buckets + ack draft + hold-to-execute */}
      <Dialog open={!!review} onClose={() => setReview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: FS.lg }}>Review deletion — {review?.request?.email}</DialogTitle>
        <DialogContent dividers>
          {draft && (
            <Box sx={{ mb: SP.section }}>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.tight }}>Acknowledgement (draft)</Typography>
              <Alert severity="info" sx={{ mb: SP.tight }}>Not sent. Send manually.</Alert>
              <TextField
                multiline fullWidth minRows={4}
                value={draft.body || ''}
                InputProps={{ readOnly: true }}
                inputProps={{ 'aria-label': 'Acknowledgement draft' }}
              />
              {/* Bis K-F2 lief die Rückmeldung über `toast`; der geteilte
                  Baustein meldet am Knopf selbst, wie jede andere Kopierstelle
                  im Baum. Der Toast fiel damit weg. */}
              <CopyButton
                value={draft.body || ''}
                text="Copy"
                label="Copy acknowledgement draft"
                sx={{ mt: 0.5 }}
                statusVisible
              />
            </Box>
          )}
          <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.tight }}>Anonymize ({totalAnon})</Typography>
          <BucketTable data={review?.preview?.to_anonymize} />
          <Typography sx={{ fontSize: FS.md, fontWeight: 600, mt: SP.section, mb: SP.tight }}>Retain + lock ({totalRestrict})</Typography>
          <BucketTable data={review?.preview?.to_restrict} />
          <Alert severity="warning" sx={{ mt: SP.tight }}>
            Anonymizes {totalAnon} account rows, locks {totalRestrict} retained rows. Not reversible.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(null)}>Cancel</Button>
          <HoldButton label="Hold to delete" labelActive="Keep holding…" labelDone="Deleted" color="error" onConfirm={runExecute} />
        </DialogActions>
      </Dialog>

      {/* Reject dialog — internal reason, no customer mail */}
      <Dialog open={!!rejectFor} onClose={() => setRejectFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: FS.lg }}>Reject request</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: SP.tight }}>Internal note only. No email goes to the customer.</Alert>
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectFor(null); setRejectReason('') }}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!rejectReason.trim()} onClick={submitReject}>Reject</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
